"use client";
import { Fragment, Suspense } from "react";
import dynamic from "next/dynamic";
import { Stack, CardHeader, Box, Grid, Typography } from "@mui/material";
import {
  DownloadRounded as DownloadIcon,
  StarRounded as LikesIcon
} from "@mui/icons-material";

import { useStore } from "../../../service/mobx";
import FilledCard from "../Filled";
import { ModelIcon } from "../../Icon/Model";
import Widget from "./Widget";
import ToolTip from "../../Tooltip";
import NextAvatar from "../../Avatar";
import { LinkNoScroll, ShallowLink } from "../../Link";
import Skeleton from "../../Skeleton";

const Popup = dynamic(() => import("../../Popup"));
const ChipAssist = dynamic(() => import("../../Chip/Assist"));
const GameBadge = dynamic(() => import("../../GameBadge"));

export function ModelCardSize1({ model }) {
  return (
    <Popup
      eventName="Model"
      maxWidth={640}
      Component={() => <ModelCardSize2 model={model} />}
    >
      <div>
        <ChipAssist label={model?.alias} onClick={() => {}} />
      </div>
    </Popup>
  );
}

export const ModelCardSize2 = ({
  entity,
  noScroll,
  model = entity,
  disabled
}) => (
  <Card
    noScroll={noScroll}
    disabled={disabled}
    model={model}
    tone="var(--surface-color)"
    sx={{
      p: 1,
      border: 1,
      borderColor: "var(--outline-variant)"
    }}
  >
    <Header
      published
      params
      size="small"
      model={model}
      task={entity === undefined}
      noGameBadge={entity !== undefined}
    />
  </Card>
);
export const ModelCardSize25 = ({ model, ...cardProps }) => (
  <Card
    model={model}
    LinkComponent={ShallowLink}
    tone="var(--surface-color)"
    sx={{
      p: 1,
      border: 1,
      borderColor: "var(--outline-variant)"
    }}
    {...cardProps}
  >
    <Header
      published
      params
      size="small"
      task
      noGameBadge
      freeBadge
      closeSourceBadge={model.closedSource}
      model={model}
      downloads
      likes
    />
  </Card>
);
export function ModelCardSize3({ model, disabled }) {
  return (
    <Card disabled={disabled} model={model}>
      <Header
        noGameBadge
        published
        params
        modelSize
        size="small"
        model={model}
      />
    </Card>
  );
}

export function ModelCardSize4({ model }) {
  return (
    <Card
      model={model}
      tone="var(--surface-color)"
      sx={{ border: 1, borderColor: "var(--outline-variant)" }}
    >
      <Header
        published
        params
        modelSize
        likes
        task
        downloads
        size="large"
        model={model}
      />
    </Card>
  );
}
export const ModelCardSize5 = ({ model }) => (
  <Card model={model}>
    <Header
      published
      params
      modelSize
      likes
      task
      downloads
      size="large"
      model={model}
    />
  </Card>
);
export function ModelCardSize6({ model }) {
  return (
    <Card model={model}>
      <Grid container sx={{ maxWidth: 640 }}>
        <Grid size={{ compact: 6 }} pr={2}>
          <Widget small model={model} />
        </Grid>
        <Grid
          size={{ compact: 6 }}
          display="flex"
          height="auto"
          flexDirection="column"
        >
          <Header
            noStar
            published
            params
            task
            modelSize
            size="large"
            model={model}
          />
          <Stack direction="row" justifyContent="space-between" />
        </Grid>
      </Grid>
    </Card>
  );
}

function Card({ children, noScroll, disabled, model, onClick, ...cardProps }) {
  const { analytics } = useStore();

  return (
    <FilledCard
      disabled={disabled}
      href={`/model/${model.name}`}
      LinkComponent={noScroll ? LinkNoScroll : undefined}
      tone="var(--surface-container-lowest)"
      onClick={() => {
        onClick?.();
        analytics.track.event("Model Click", { modelId: model.name });
      }}
      {...cardProps}
    >
      {children}
    </FilledCard>
  );
}

const Header = ({
  model,
  Status,
  size = "medium",
  noStar,
  noGameBadge,
  ...subheaderProps
}) => (
  <>
    <CardHeader
      avatar={
        <ModelAvatar
          noGameBadge={noGameBadge}
          model={model}
          avatar={`/api/model/avatar?org=${model.org}${
            model.repo?.hfOwner?.avatar
              ? `&imgUrl=${btoa(model.repo?.hfOwner?.avatar)}`
              : ""
          }`}
        />
      }
      title={model.name.replace(`${model.org}/`, "")}
      subheader={<SubHeader model={model} size={size} {...subheaderProps} />}
      slotProps={{
        title: {
          variant: "headlineMd",
          color: "var(--surface-on-color)",
          sx: theme => theme.typography.lineClamp(3),
          typography: {
            compact: size === "small" ? "labelMd" : "titleMd",
            expanded:
              size === "small"
                ? "labelLg"
                : size === "large"
                  ? "titleSm"
                  : size === "xl"
                    ? "titleLg"
                    : "titleSm",
            large:
              size === "small"
                ? "labelLg"
                : size === "large"
                  ? "titleMd"
                  : size === "xl"
                    ? "headlineMd"
                    : "titleMd"
          }
        },
        subheader: {
          color: "var(--secondary-color)",
          typography: {
            compact: "bodySm",
            expanded:
              size === "xl" ? "bodyLg" : size === "small" ? "bodySm" : "bodyMd"
          }
        }
      }}
      action={
        model.badge ? (
          <Box
            typography="labelMd"
            mt={0.5}
            px={1}
            py={0.5}
            mx={1}
            bgcolor="var(--colors-info-90)"
            color="var(--colors-info-10)"
            borderRadius="var(--shape-xs-round)"
            display={{ compact: "none", extraLarge: "unset" }}
          >
            {model.badge}
          </Box>
        ) : null
      }
      sx={{ p: 0, alignItems: "flex-start" }}
    />
    {model.badge ? (
      <Box
        px={1}
        py={0.5}
        width="fit-content"
        bgcolor="var(--colors-info-90)"
        color="var(--colors-info-10)"
        borderRadius="var(--shape-xs-round)"
        ml="56px"
        display={{ compact: "unset", extraLarge: "none" }}
        typography={{ compact: "labelSm", expanded: "labelMd" }}
      >
        {model.badge}
      </Box>
    ) : null}
  </>
);

export function SubHeader({
  model,
  likes = false,
  downloads = false,
  org = true,
  params = false,
  published = true,
  task = false,
  freeBadge = false,
  closeSourceBadge = false
}) {
  const { utilities, model: modelStore } = useStore();
  const secondary = [
    org ? model.org : undefined,
    task ? model.tasks?.[0]?.name : undefined,
    published &&
    (model.paper?.published ||
      model.repo?.created ||
      model.updated ||
      model.created)
      ? utilities.paperTime(
          model.paper?.published ||
            model.repo?.created ||
            model.updated ||
            model.created
        )
      : undefined,
    params && model.params
      ? `${utilities.formatNumber(model.params)} params`
      : undefined
  ].filter(defined => defined);

  return (
    <Fragment key={model.id}>
      <Stack py={0.5} direction="row" alignItems="center" flexWrap="wrap">
        {secondary.map((content, index, array) => (
          <Fragment key={index}>
            <Box
              component="span"
              color="var(--secondary-color)"
              borderRadius={1000}
            >
              {content}
            </Box>
            {index + 1 === array.length ? undefined : (
              <Box component="span" mx={0.5}>
                ·
              </Box>
            )}
          </Fragment>
        ))}
      </Stack>
      <Stack pt={1} direction="row" spacing={1} alignItems="center">
        <ToolTip
          placement="top"
          title={`This is an ${
            closeSourceBadge ? "closed" : "open"
          } source model`}
        >
          <Typography
            variant="labelSm"
            px={1}
            py={0.5}
            borderRadius="var(--shape-xs-round)"
            border="1px solid var(--outline-variant)"
          >
            {closeSourceBadge ? "closed" : "open"}
          </Typography>
        </ToolTip>
        {freeBadge && modelStore.isFreeTier(model) ? (
          <ToolTip title="Available on the free tier" placement="top">
            <Stack
              px={1}
              py={0.5}
              direction="row"
              bgcolor="#DDF8D8"
              spacing={0.5}
              alignItems="center"
              borderRadius="var(--shape-xs-round)"
            >
              <Typography color="var(--colors-success-30)" variant="labelSm">
                free tier
              </Typography>
            </Stack>
          </ToolTip>
        ) : null}
        {likes && model.repo?.likes ? (
          <StatChip
            tooltip="Likes"
            placement="top"
            Icon={LikesIcon}
            count={utilities.formatNumber(model.repo.likes)}
          />
        ) : null}

        {downloads && model.repo?.downloads ? (
          <StatChip
            tooltip="Downloads"
            placement="top"
            Icon={DownloadIcon}
            count={utilities.formatNumber(model.repo.downloads)}
          />
        ) : null}
      </Stack>
    </Fragment>
  );
}

const StatChip = ({ count, tooltip, Icon, placement }) => (
  <ToolTip title={tooltip} placement={placement}>
    <Stack
      spacing={0.5}
      typography="labelSm"
      pl={1}
      pr={1.25}
      py={0.5}
      direction="row"
      alignItems="center"
      borderRadius="var(--shape-xs-round)"
      bgcolor="var(--secondary-container)"
      color="var(--secondary-on-container)"
    >
      <Icon
        sx={{
          width: 16,
          height: 16,
          color: "var(--secondary-color)"
        }}
      />
      <span>{count}</span>
    </Stack>
  </ToolTip>
);

export const ModelAvatar = ({ noGameBadge, model, avatar }) =>
  noGameBadge !== true && model.repo?.scoreTrending ? (
    <Suspense
      fallback={<Skeleton height={40} width={40} borderRadius="xs-round" />}
    >
      <GameBadge scoreTrending={model.repo?.scoreTrending} />
    </Suspense>
  ) : (
    <NextAvatar
      size={40}
      alt={model.org}
      src={avatar}
      priority={false}
      variant="rounded"
      fallback={<ModelIcon sx={{ color: "var(--surface-on-variant)" }} />}
    />
  );
